import { Badge } from '@cimplico/components';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { TemplateProcedureOverview } from '../../types/procedure';
import CategoryDeleteConfirmDialog from './CategoryDeleteConfirmDialog';
import EditProcedureCategoryConfirm from './EditCategoryConfirmDialog';
import ProcedureRow from './ProcedureRow';

type ProcedureCategorySectionProps = {
    templateId: string;
    isDisabled?: boolean;
    selectedProcedures?: string[];
    categoryDetails: { categoryId?: string; categoryName: string; procedures: TemplateProcedureOverview[] };
    onChecked: (id: string) => void;
};

function ProcedureCategorySection({
    templateId,
    isDisabled,
    selectedProcedures,
    categoryDetails,
    onChecked,
}: ProcedureCategorySectionProps): JSX.Element {
    const [showProcedures, setShowProcedures] = useState(true);
    const [action, setAction] = useState<string | undefined>();

    function handleToggleShowProcedures(): void {
        setShowProcedures(!showProcedures);
    }

    function clearAction(): void {
        setAction(undefined);
    }

    return (
        <>
            <EditProcedureCategoryConfirm
                templateId={templateId}
                categoryId={categoryDetails.categoryId}
                defaultCategoryName={categoryDetails.categoryName}
                isOpen={action === 'EDIT'}
                onClose={clearAction}
            />
            <CategoryDeleteConfirmDialog
                templateId={templateId}
                categoryId={categoryDetails.categoryId}
                isOpen={action === 'DELETE'}
                onClose={clearAction}
            />
            <div className="py-4 border-b flex items-center justify-between">
                <div className="flex">
                    <p className="text-gray-500 text-sm font-medium">{categoryDetails.categoryName}</p>
                    <span className="ml-1 -mt-1">
                        <Badge label={categoryDetails.procedures.length.toString()} shape="rounded" size="small" color="gray" />
                    </span>
                    <button className="ml-2" onClick={handleToggleShowProcedures}>
                        {showProcedures ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronUpIcon className="h-4 w-4" />}
                    </button>
                </div>
                {categoryDetails.categoryId && !isDisabled && (
                    <div className="flex text-indigo-600 text-sm gap-4">
                        <button onClick={(): void => setAction('EDIT')}>Edit</button>
                        <div className="w-0.5 bg-gray-300" />
                        <button onClick={(): void => setAction('DELETE')}>Delete</button>
                    </div>
                )}
            </div>

            {showProcedures && (
                <div className="pl-4">
                    {categoryDetails.procedures
                        .sort((a, b) => (a.sequence > b.sequence ? 1 : -1))
                        .map((procedure) => {
                            return (
                                <ProcedureRow
                                    key={procedure.id}
                                    procedure={procedure}
                                    onChecked={onChecked}
                                    isDisabled={isDisabled}
                                    checked={selectedProcedures?.includes(procedure.id)}
                                />
                            );
                        })}
                </div>
            )}
        </>
    );
}

export default ProcedureCategorySection;
